import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-transparent font-poppins text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-green focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-brand-green text-white hover:bg-brand-base',
        'dark-green': 'bg-brand-dark text-white hover:bg-brand-dark/95',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        'destructive-outline':
          'border border-destructive text-destructive hover:bg-destructive/10 hover:text-destructive',
        outline:
          'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        'outline-brand':
          'border border-brand-green bg-background hover:bg-accent hover:text-accent-foreground',
        secondary:
          'border border-brand-green bg-background text-brand-green hover:bg-brand-light hover:text-brand-base',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        'badge-close': 'hover:bg-brand-base [&_svg]:size-3',
        transparent: 'hover:bg-transparent hover:text-brand-green',
        'pagination-active':
          'bg-brand-green text-white border-brand-green hover:bg-brand-base',
        'header-icon':
          'rounded-xl bg-white border border-gray-200 text-brand-green [&_svg]:size-6 hover:bg-gray-50 hover:text-brand-green shadow-sm',
        'header-icon-outline':
          'rounded-xl bg-white border border-gray-200 text-brand-green [&_svg]:size-6 hover:bg-gray-50 hover:text-brand-green shadow-sm',
        'dashboard-pagination':
          'bg-[#ECECEB] text-[#BAB5B5] rounded border border-[#E2E2E2] disabled:opacity-75 [&_svg]:size-5 font-medium text-[0.6875rem]',
        'dashboard-pagination-active':
          'bg-brand-green hover:bg-brand-base text-white rounded',
        'secondary-dark': 'bg-[#282F3C] text-white hover:bg-[#282F3C]/90',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-lg px-3',
        lg: 'h-11 rounded-lg px-8',
        'icon-xs': 'size-4',
        'icon-sm': 'size-6',
        icon: 'size-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
